import React, { useState } from 'react';
import { Input, Button, Divider } from '@nextui-org/react';
import { useAuth } from '../../hooks/useAuth';
import GoogleAuthButton from './GoogleAuthButton';

const SignupForm: React.FC = () => {
    const { signup, error, loginWithGoogle } = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [validationError, setValidationError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError('');

        // Validate passwords match
        if (password !== confirmPassword) {
            setValidationError('Passwords do not match');
            return;
        }

        // Validate password length
        if (password.length < 6) {
            setValidationError('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);

        try {
            await signup(email, password, name);
        } catch (err) {
            // Error is handled in AuthContext
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        try {
            await loginWithGoogle();
        } catch (err) {
            // Error is handled in AuthContext
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 py-4">
            <Input
                label="Full Name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="bordered"
                isRequired
                autoComplete="name"
                classNames={{
                    label: "text-black dark:text-white",
                    input: "text-black dark:text-white",
                }}
            />

            <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="bordered"
                isRequired
                autoComplete="email"
                classNames={{
                    label: "text-black dark:text-white",
                    input: "text-black dark:text-white",
                }}
            />

            <Input
                label="Password"
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="bordered"
                isRequired
                autoComplete="new-password"
                classNames={{
                    label: "text-black dark:text-white",
                    input: "text-black dark:text-white",
                }}
                description="Password must be at least 6 characters"
            />

            <Input
                label="Confirm Password"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                variant="bordered"
                isRequired
                autoComplete="new-password"
                classNames={{
                    label: "text-black dark:text-white",
                    input: "text-black dark:text-white",
                }}
            />

            {(error || validationError) && (
                <p className="text-red-500 text-sm">{error || validationError}</p>
            )}

            <Button
                type="submit"
                color="primary"
                variant="solid"
                fullWidth
                isLoading={isLoading}
                className="bg-blue-500 text-white"
            >
                Sign Up
            </Button>

            <div className="relative">
                <Divider className="my-4" />
                <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 px-2 text-sm text-gray-500">
                    OR
                </span>
            </div>

            <GoogleAuthButton onClick={handleGoogleLogin} isLoading={isLoading} />
        </form>
    );
};

export default SignupForm; 